import type { MakeStylesHook, StyleRules } from '@egr/xbox/utils/tss-react';
import type { Theme, SxProps } from '@mui/material/styles';

import Check from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import * as React from 'react';

import { languageStore } from '../../../stores/LanguageStore';

import { AppBarButton } from './AppBarButton';

import { languageToLabelComponent } from '@egr/xbox/egr-gui-settings/components/LanguageChoice';
import { makeStyles } from '@egr/xbox/utils/tss-react';

type AppBarLanguageButtonClasses = 'button' | 'icon' | 'listItemIcon';

function getAppBarLanguageButtonStyles(): StyleRules<AppBarLanguageButtonClasses> {
    return {
        button: {
            lineHeight: '1.2em',
            fontWeight: 600,
        },
        icon: {
            marginRight: 12,
            width: 18,
            height: 18,
        },
        listItemIcon: {
            minWidth: 48,
        },
    };
}

const useAppBarLanguageButtonStyles: MakeStylesHook<AppBarLanguageButtonClasses> = makeStyles({ name: 'AppBarLanguageButton' })(getAppBarLanguageButtonStyles);

interface AppBarLanguageButtonProps {
    disabled?: boolean;
    iconStyle?: SxProps<Theme>;
}

export const AppBarLanguageButton: React.FC<AppBarLanguageButtonProps> = ({ disabled, iconStyle }) => {
    const { classes } = useAppBarLanguageButtonStyles();
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLButtonElement>(null);
    const { language } = languageStore.useState(['language']);

    const languages = languageStore.userLanguageOptions;
    const button: React.ReactNode =
        iconStyle == null ? (
            <Tooltip
                title={language}
                placement="bottom"
            >
                <span>
                    <IconButton
                        ref={ref}
                        sx={{ color: '#FFFFFF' }}
                        onClick={() => setOpen(true)}
                    >
                        <LanguageIcon />
                    </IconButton>
                </span>
            </Tooltip>
        ) : (
            <AppBarButton
                ref={ref}
                onClick={() => setOpen(true)}
                disabled={disabled ?? false}
                icon={<LanguageIcon sx={iconStyle} />}
                label={language}
            />
        );

    return (
        <>
            {button}
            <Menu
                anchorEl={ref.current}
                open={open}
                onClose={() => setOpen(false)}
            >
                {languages.map((languageEntry: string) => (
                    <MenuItem
                        data-testid={`languageCode-${languageEntry}`}
                        key={languageEntry}
                        onClick={() => {
                            languageStore.updateLanguage(languageEntry);
                            setOpen(false);
                        }}
                        sx={{ minWidth: 250, padding: '10px' }}
                    >
                        <ListItemIcon className={classes?.listItemIcon}>
                            {language === languageEntry && (
                                <Check
                                    fontSize="small"
                                    sx={{ color: '#868686' }}
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText primary={languageToLabelComponent(languageEntry)} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

AppBarLanguageButton.displayName = 'AppBarLanguageButton';